import Vue from 'vue';
// See https://github.com/mblarsen/vue-browser-acl
import Acl from 'vue-browser-acl';
import { UserInfo } from '@/types';

import router from '@/config/router/router';
import setPagePermission from '@/config/security/PagePermission';
import {
	AccessMasterRolesEnum,
	BusinessRolesEnum
} from '@/types/enum/RoleEnum';
import setActionPermission from '@/config/security/ActionPermission';
import { PermissionEnum } from '@/types/enum/PermissionEnum';
import appStateUpdator from '@/store/AppStateUpdator';

/**
 * Get user from store
 * This needs to be a function because the user
 * is retrieved asynchronously
 */
const user = () => appStateUpdator.getUser() as UserInfo;

/**
 * Set rules
 * This needs to be a function because it's asynchronous
 */
function permissions(acl: any) {
	acl.subjectMapper = (s: any) => {
		return 'string' === typeof s ? s : s.type;
	};

	acl = setPagePermission(acl);
	acl = setActionPermission(acl);

	acl.rule(PermissionEnum.NATIONAL_ADMIN, (user: UserInfo) =>
		user &&
		user.roles.includes(AccessMasterRolesEnum.NATIONAL_ADMIN)
	);

	acl.rule(PermissionEnum.NATIONAL_VALIDATOR, (user: UserInfo) =>
		user &&
		user.roles.includes(AccessMasterRolesEnum.NATIONAL_VALIDATOR)
	);

	acl.rule(PermissionEnum.NATIONAL_DGOS, (user: UserInfo) =>
		user &&
		user.roles.includes(AccessMasterRolesEnum.NATIONAL_DGOS)
	);

	acl.rule(PermissionEnum.LIQUIDATOR, (user: UserInfo) =>
		user &&
		user.roles.includes(AccessMasterRolesEnum.LIQUIDATOR)
	);

	acl.rule(PermissionEnum.LIQUIDATOR_MANAGER, (user: UserInfo) =>
		user &&
		(
			user.roles.includes(AccessMasterRolesEnum.LIQUIDATOR_MANAGER)
		)
	);

	acl.rule(PermissionEnum.CPAM_SUPERVISOR, (user: UserInfo) =>
		user &&
		(
			user.roles.includes(AccessMasterRolesEnum.CPAM_SUPERVISOR)
		)
	);

	acl.rule(PermissionEnum.EVALUATION_TC, (user: UserInfo) =>
		user &&
		(
			user.roles.includes(AccessMasterRolesEnum.EVALUATION_TC)
		)
	);

	acl.rule(PermissionEnum.EVALUATION_DM, (user: UserInfo) =>
		user &&
		(
			user.roles.includes(AccessMasterRolesEnum.EVALUATION_DM)
		)
	);

	/**
	 * Check if it is an accessmaster admin,
	 * Or if it's an local admin who belong in the project
	 */
	acl.rule('admin_local', 'Project', (user: UserInfo, project: any) => {
		return (
			user &&
			(
				hasRolesForProject(BusinessRolesEnum.ADMIN_LOCAL, user, project)
			));
	});
}

export function hasRolesForProject(role: string, user: UserInfo, project?: any) {
	if (null === user.projectUsers || null === project.token) {
		return false;
	}

	const projectUser = user.projectUsers.find((projectUser) => {
		return projectUser.project.token === project.token;
	});

	return Boolean(projectUser && projectUser.roles.includes(role));
}

// Resolve user & permissions asynchronously
Vue.use(Acl, user, (acl: any) => permissions(acl), {
	router,
	failRoute: {
		name: 'unauthorized',
		replace: true
	}
});

export default Acl;
